'use client';
import { useEventCallback, useIsomorphicLayoutEffect } from "@fluentui/react-utilities";
import * as React from "react";
import { useAnimateAtoms } from "../hooks/useAnimateAtoms.js";
import { useMotionImperativeRef } from "../hooks/useMotionImperativeRef.js";
import { useIsReducedMotion } from "../hooks/useIsReducedMotion.js";
import { useChildElement } from "../utils/useChildElement.js";
import { useMotionBehaviourContext } from "../contexts/MotionBehaviourContext.js";
/**
 * A private symbol to store the motion definition on the component for variants.
 *
 * @internal
 */ export const MOTION_DEFINITION = Symbol('MOTION_DEFINITION');
/**
 * Creates a component that will animate the children using the provided motion.
 *
 * @param value - A motion definition.
 */ export function createMotionComponent(value) {
    const Atom = (props)=>{
        const { children, imperativeRef, onMotionFinish: onMotionFinishProp, onMotionStart: onMotionStartProp, onMotionCancel: onMotionCancelProp, replayKey, ..._rest } = props;
        const params = _rest;
        const [child, childRef] = useChildElement(children);
        const handleRef = useMotionImperativeRef(imperativeRef);
        const isInitialRender = React.useRef(true);
        const skipMotions = useMotionBehaviourContext() === 'skip';
        const optionsRef = React.useRef({
            skipMotions,
            params
        });
        const animateAtoms = useAnimateAtoms();
        const isReducedMotion = useIsReducedMotion();
        const onMotionStart = useEventCallback(()=>{
            onMotionStartProp === null || onMotionStartProp === void 0 ? void 0 : onMotionStartProp(null);
        });
        const onMotionFinish = useEventCallback(()=>{
            onMotionFinishProp === null || onMotionFinishProp === void 0 ? void 0 : onMotionFinishProp(null);
        });
        const onMotionCancel = useEventCallback(()=>{
            onMotionCancelProp === null || onMotionCancelProp === void 0 ? void 0 : onMotionCancelProp(null);
        });
        // Stable callback (all deps are refs or useEventCallback) that activates a handle for a new playback cycle.
        //
        // TODO: consider moving the cancel+play+rewire sequence into a handle.replay() method on AnimationHandle,
        // keeping pure animation sequencing on the handle and React callbacks here in the component.
        const activateAnimationHandle = React.useCallback((handle)=>{
            onMotionStart();
            handle.setMotionEndCallbacks(onMotionFinish, onMotionCancel);
            if (optionsRef.current.skipMotions) {
                handle.finish();
            }
        }, [
            onMotionStart,
            onMotionFinish,
            onMotionCancel
        ]);
        useIsomorphicLayoutEffect(()=>{
            // Heads up!
            // We store the params in a ref to avoid re-rendering the component when the params change.
            optionsRef.current = {
                skipMotions,
                params
            };
        });
        useIsomorphicLayoutEffect(()=>{
            const element = childRef.current;
            if (element) {
                const atoms = typeof value === 'function' ? value({
                    element,
                    ...optionsRef.current.params
                }) : value;
                const handle = animateAtoms(element, atoms, {
                    isReducedMotion: isReducedMotion()
                });
                handleRef.current = handle;
                activateAnimationHandle(handle);
                return ()=>{
                    handle.cancel();
                };
            }
        }, [
            animateAtoms,
            childRef,
            handleRef,
            isReducedMotion,
            activateAnimationHandle
        ]);
        // Skips initial mount; on replayKey changes, reuses existing Animation objects via cancel+play
        // rather than recreating them, preserving DOM continuity.
        useIsomorphicLayoutEffect(()=>{
            if (isInitialRender.current) {
                return;
            }
            const handle = handleRef.current;
            if (handle) {
                handle.cancel();
                handle.play();
                activateAnimationHandle(handle);
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- replayKey is intentionally the only trigger; other deps are stable refs/callbacks
        }, [
            replayKey
        ]);
        useIsomorphicLayoutEffect(()=>{
            isInitialRender.current = false;
            return ()=>{
                isInitialRender.current = true;
            };
        }, []);
        return child;
    };
    return Object.assign(Atom, {
        // Heads up!
        // Always normalize it to a function to simplify types
        [MOTION_DEFINITION]: typeof value === 'function' ? value : ()=>value
    });
}
